/* SPEC-509 — the Updates feed (system notices: data runs finished, drift, guardrails). It was
 * the top-right bell; the bell is gone and this feed now has two readers: the Alerts badge on
 * the rail (unread count → the amber dot) and the "Updates" tab on the Alerts page (the list).
 *
 * Honesty change from the bell: the bell swallowed a failed load and showed no badge. Here a
 * failed or non-OK response is an ERROR that callers must show ("!" on the badge, a message
 * in the tab) — never a clean zero. */
import { useCallback, useEffect } from 'react';
import { useApi } from './useApi';

export interface UpdateItem {
  id: number;
  title: string;
  message: string;
  category: string;
  is_read: boolean;
  created_at: string;
}

/** Fired after a mark-read, so every reader of the feed re-reads it at once. */
export const UPDATES_CHANGED_EVENT = 'suryaiq:updates-changed';

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('suryaiq_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function getJson<T>(url: string): Promise<T> {
  const res = await fetch(url, { headers: authHeaders() });
  if (!res.ok) throw new Error(`Updates could not load (${res.status})`);
  return res.json() as Promise<T>;
}

async function fetchUnread(): Promise<number> {
  const data = await getJson<{ unread?: unknown }>('/api/notifications/unread');
  if (typeof data.unread !== 'number') throw new Error('Updates could not load (no count)');
  return data.unread;
}

/** How many notices the Updates tab shows at first, each "Show older" step, and at most
 *  (the endpoint's own cap). */
export const UPDATES_PAGE = 20;
export const UPDATES_MAX = 200;

async function fetchList(limit: number): Promise<UpdateItem[]> {
  const data = await getJson<{ notifications?: UpdateItem[] }>(`/api/notifications?limit=${limit}`);
  return Array.isArray(data.notifications) ? data.notifications : [];
}

async function post(url: string): Promise<void> {
  const res = await fetch(url, { method: 'POST', headers: authHeaders() });
  if (!res.ok) throw new Error('That did not save — try again');
  window.dispatchEvent(new Event(UPDATES_CHANGED_EVENT));
}

export const markUpdateRead = (id: number) => post(`/api/notifications/${id}/read`);
export const markAllUpdatesRead = () => post('/api/notifications/read-all');

/** Re-run `refresh` whenever another reader marks something read. */
function useRefreshOnChange(refresh: () => void) {
  useEffect(() => {
    const on = () => refresh();
    window.addEventListener(UPDATES_CHANGED_EVENT, on);
    return () => window.removeEventListener(UPDATES_CHANGED_EVENT, on);
  }, [refresh]);
}

/** The unread count for the badge: `unread` null until the first answer; `error` true when
 *  the latest read failed. Polls every minute, as the bell did. */
export function useUpdatesUnread(enabled = true): { unread: number | null; error: boolean } {
  const q = useApi(fetchUnread, [], { refreshInterval: 60_000, enabled });
  useRefreshOnChange(q.refresh);
  return { unread: q.error ? null : q.data, error: !!q.error };
}

/** The list for the Updates tab, newest first, `limit` long. Fetched only while the tab is open. */
export function useUpdatesList(enabled: boolean, limit: number = UPDATES_PAGE) {
  const q = useApi(() => fetchList(limit), [limit], { refreshInterval: 120_000, enabled });
  useRefreshOnChange(q.refresh);
  const refresh = useCallback(() => q.refresh(), [q.refresh]);
  return { items: q.data, loading: q.loading, error: q.error, refresh };
}
